import React, { useContext, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { AuthContext } from "shared/context/auth-context";
import { StoreContext } from "shared/context/store-context";
import { trackSuiteEvent } from "shared/util/track";
import { START_STAGES, useStartJourney } from "features/start/useStartJourney";
import { markStartSeen, consumePostAuthPath } from "features/start/start-flags";
import StartHero from "features/start/components/StartHero";
import StageUnderstand from "features/start/components/StageUnderstand";
import StagePrepare from "features/start/components/StagePrepare";
import StageVisualize from "features/start/components/StageVisualize";
import StageApps from "features/start/components/StageApps";
import StageContinue from "features/start/components/StageContinue";
import StartProcessing from "features/start/components/StartProcessing";
import "./StartExperience.css";

const STAGE_LABELS = {
  welcome: "Welcome",
  understand: "Spreadsheet",
  prepare: "Operation",
  visualize: "Routes",
  apps: "Apps",
  continue: "Continue",
};

// The page head speaks the app's language; the dark canvas below it is where the import happens.
const PAGE_COPY = {
  welcome: { title: "Start with a spreadsheet", subtitle: "Turn a student list into passengers, parents and services." },
  understand: { title: "Import a spreadsheet", subtitle: "Check what was read and decide what to do with it." },
  prepare: { title: "Import a spreadsheet", subtitle: "Set the school and the vehicle capacity." },
  visualize: { title: "Import a spreadsheet", subtitle: "Review the services before anything is created." },
  apps: { title: "Import a spreadsheet", subtitle: "See what drivers and parents will get." },
  continue: { title: "Import a spreadsheet", subtitle: "Confirm, and the roster is written into your operation." },
};

// Marks the document while a save job runs so the navbar can step back (see MainNavigation.css).
const BODY_SAVING_CLASS = "is-start-saving";

const StartExperience = () => {
  const auth = useContext(AuthContext);
  const store = useContext(StoreContext);
  const navigate = useNavigate();
  const journey = useStartJourney({ isLoggedIn: Boolean(auth.isLoggedIn), clientId: auth.id || null });

  useEffect(() => {
    trackSuiteEvent("Start — Opened", "page_view", { key: "start_opened" });
    consumePostAuthPath();
  }, []);

  // Prefer the server's count from the last match check; the store can lag right after an import.
  const existingRouteCount = journey.match?.company?.routes?.length ?? (store?.storeReady ? store.vehicleRoutes?.length || 0 : 0);

  const skipToDashboard = () => {
    markStartSeen(auth.id);
    trackSuiteEvent("Start — Skip to dashboard", "button_click");
    navigate("/dashboard");
  };

  // A save job in flight (either path): the navbar dims and the head explains that leaving is safe.
  const saving = Boolean(journey.jobProgress || journey.workspaceStep);
  useEffect(() => {
    if (typeof document === "undefined") return undefined;
    document.body.classList.toggle(BODY_SAVING_CLASS, saving);
    return () => document.body.classList.remove(BODY_SAVING_CLASS);
  }, [saving]);

  const copy =
    journey.source === "sample" && journey.stage !== "welcome"
      ? { title: "Sample roster", subtitle: "85 students across three schools, written into a private workspace of your own." }
      : PAGE_COPY[journey.stage] || PAGE_COPY.welcome;
  const showStages = journey.stage !== "welcome" && !journey.processing;

  return (
    <div className="start-page">
      <header className="start-page__head">
        <div className="start-page__heading">
          <h1 className="start-page__title">{copy.title}</h1>
          <p className="start-page__subtitle">
            {saving ? "Saving continues on the server even if you leave this page — you can come back to it any time." : copy.subtitle}
          </p>
        </div>
        {showStages && (
          <ol className="start-progress" aria-label="Import stages">
            {(journey.stages || START_STAGES).filter((stage) => stage !== "welcome").map((stage) => (
              <li key={stage} aria-current={journey.stage === stage ? "step" : undefined}>
                <button
                  type="button"
                  className={journey.stage === stage ? "is-active" : ""}
                  onClick={() => journey.goTo(stage)}
                  disabled={(!journey.parsed && stage !== "continue") || journey.busy}
                >
                  {STAGE_LABELS[stage]}
                </button>
              </li>
            ))}
          </ol>
        )}
      </header>

      <section className="start-exp" aria-label="Spreadsheet import">
      <div className="start-exp__body">
        {journey.processing ? (
          <StartProcessing script={journey.processing.script} />
        ) : (
          <>
            {journey.stage === "welcome" && (
              <StartHero
                onFile={journey.loadFile}
                onSample={() => {
                  trackSuiteEvent("Start — Try sample", "button_click");
                  journey.loadSample();
                }}
                busy={journey.busy}
                error={journey.error}
                authenticated={auth.isLoggedIn}
                isDemo={Boolean(auth.company?.isDemo)}
                onSkipDashboard={skipToDashboard}
              />
            )}
            {journey.stage === "understand" && (
              <StageUnderstand
                fileName={journey.fileName}
                parsed={journey.parsed}
                mapping={journey.mapping}
                preview={journey.preview}
                source={journey.source}
                extraction={journey.extraction}
                interpreter={journey.interpreter}
                schoolName={journey.schoolName}
                schoolAddress={journey.schoolAddress}
                schoolDecision={journey.schoolDecision}
                onSchoolName={journey.setSchoolName}
                onSchoolAddress={journey.setSchoolAddress}
                onSchoolDecision={journey.setSchoolDecision}
                canAdvance={journey.canAdvanceFromUnderstand}
                onNext={journey.goNext}
                onBack={() => journey.goTo("welcome")}
                onFile={journey.loadFile}
                replaceError={journey.error}
                replaceBusy={journey.busy}
                intent={journey.intent}
                intentSuggestion={journey.intentSuggestion}
                onIntent={journey.setIntent}
                includeRouteList={journey.includeRouteList}
                onIncludeRouteList={journey.setIncludeRouteList}
                existingMode={journey.existingMode}
                onExistingMode={journey.setExistingMode}
                existingDecisions={journey.existingDecisions}
                onExistingDecision={journey.setExistingDecision}
                match={journey.match}
                matchBusy={journey.matchBusy}
                isLoggedIn={Boolean(auth.isLoggedIn)}
                onUpdatePassenger={journey.updatePassenger}
                onRemovePassenger={journey.removePassenger}
                onRemoveRows={journey.removeRows}
                undoStack={journey.undoStack}
                onUndo={journey.undoRemove}
                onApplyMapping={journey.applyManualMapping}
              />
            )}
            {journey.stage === "prepare" && (
              <StagePrepare
                preview={journey.preview}
                source={journey.source}
                extraction={journey.extraction}
                schoolName={journey.schoolName}
                schoolAddress={journey.schoolAddress}
                schoolDecision={journey.schoolDecision}
                schoolRequired={journey.schoolRequired}
                maxPassengers={journey.maxPassengers}
                serviceDirection={journey.serviceDirection}
                directionSuggestion={journey.directionSuggestion}
                onServiceDirection={journey.setServiceDirection}
                onMaxPassengers={journey.setMaxPassengers}
                canAdvance={journey.canAdvanceFromPrepare}
                onNext={journey.goNext}
                onBack={journey.goBack}
              />
            )}
            {journey.stage === "visualize" && (
              <StageVisualize
                source={journey.source}
                preview={journey.preview}
                operation={journey.operation}
                servicePlan={journey.servicePlan}
                serviceDirection={journey.serviceDirection}
                maxPassengers={journey.maxPassengers}
                selectedRouteId={journey.selectedRouteId}
                onSelectRoute={journey.setSelectedRouteId}
                existingRouteCount={existingRouteCount}
                namedServiceCount={journey.workingExtraction?.stats?.distinctRouteNameCount || 0}
                onNext={journey.goNext}
                onBack={journey.goBack}
              />
            )}
            {journey.stage === "apps" && (
              <StageApps
                operation={journey.operation}
                selectedRoute={journey.selectedRoute}
                onSelectRoute={journey.setSelectedRouteId}
                role={journey.walkthroughRole}
                onRoleChange={journey.setWalkthroughRole}
                source={journey.source}
                onNext={journey.goNext}
                onBack={journey.goBack}
              />
            )}
            {journey.stage === "continue" && (
              <StageContinue
                onBack={journey.goBack}
                isDemo={Boolean(auth.company?.isDemo)}
                source={journey.source}
                intent={journey.intent}
                extraction={journey.workingExtraction}
                match={journey.match}
                existingMode={journey.existingMode}
                existingDecisions={journey.existingDecisions}
                includeRouteList={journey.includeRouteList}
                serviceDirection={journey.serviceDirection}
                servicePlan={journey.servicePlan}
                schoolName={journey.schoolName}
                schoolDecision={journey.schoolDecision}
                maxPassengers={journey.maxPassengers}
                commitResult={journey.commitResult}
                commitError={journey.commitError}
                existingStudentCount={journey.existingStudentCount}
                busy={journey.busy}
                onCommit={journey.commitOperation}
                onCreateWorkspace={journey.createWorkspaceAndCommit}
                onResumeCommit={journey.resumeCommit}
                pendingJob={journey.pendingJob}
                handoffSession={journey.handoffSession}
                jobProgress={journey.jobProgress}
                workspaceStep={journey.workspaceStep}
                demoKind={auth.company?.demoKind}
                fileName={journey.fileName}
              />
            )}
          </>
        )}
      </div>
      </section>
    </div>
  );
};

export default StartExperience;
